import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Input } from '@/common/ui/input'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateTelegramNotifySetting from '../../api/hooks/useMutationUpdateTelegramNotifySetting'
import { TelegramNotifyQueryKeys } from '../../keys'

import type {
	TelegramNotifySettingResponse,
	UpdateTelegramNotifySettingDto,
} from '../../types'

const formSchema = z.object({
	chatId: z.string().optional(),
	highPriorityChatId: z.string().optional(),
	bigWinThreshold: z.coerce
		.number()
		.min(0, 'ต้องมากกว่าหรือเท่ากับ 0')
		.optional(),
})

type FormValues = z.infer<typeof formSchema>

interface TelegramNotifyFormProps {
	data: TelegramNotifySettingResponse
}

export default function TelegramNotifyForm({ data }: TelegramNotifyFormProps) {
	const queryClient = useQueryClient()
	const { mutateAsync: updateTelegramNotifySetting, isPending: isFormLoading } =
		useMutationUpdateTelegramNotifySetting()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		values: {
			chatId: (data?.chatId as unknown as string) ?? '',
			highPriorityChatId: (data?.highPriorityChatId as unknown as string) ?? '',
			bigWinThreshold: data?.bigWinThreshold ?? 0,
		},
	})

	const handleFormSubmit = async (values: FormValues) => {
		try {
			await updateTelegramNotifySetting({
				chatId: values.chatId,
				highPriorityChatId: values.highPriorityChatId,
				bigWinThreshold: values.bigWinThreshold,
			} as UpdateTelegramNotifySettingDto)
			toast.success('อัพเดทการตั้งค่าการแจ้งเตือน Telegram สำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [TelegramNotifyQueryKeys.GetTelegramNotifySetting],
			})
		} catch (err) {
			showErrorToast(err as Error)
		}
	}

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleFormSubmit)}
				className="space-y-4"
			>
				<FormField
					control={form.control}
					name="chatId"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Chat ID</FormLabel>
							<FormControl>
								<Input placeholder="กรอก Telegram Chat ID" {...field} />
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="highPriorityChatId"
					render={({ field }) => (
						<FormItem>
							<FormLabel>High Priority Chat ID</FormLabel>
							<FormControl>
								<Input
									placeholder="กรอก Telegram Chat ID สำหรับการแจ้งเตือนสำคัญ"
									{...field}
								/>
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="bigWinThreshold"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								จำนวนเงินขั้นต่ำที่จะแจ้งเตือนเมื่อผู้เล่นชนะรางวัลใหญ่
							</FormLabel>
							<FormControl>
								<Input
									type="number"
									placeholder="กรอกจำนวนเงินขั้นต่ำ"
									{...field}
								/>
							</FormControl>

							<FormMessage />
						</FormItem>
					)}
				/>

				<Button type="submit" disabled={isFormLoading}>
					{isFormLoading ? 'กำลังบันทึก...' : 'บันทึก'}
				</Button>
			</form>
		</Form>
	)
}
